import React, { useEffect, useState, useCallback, useRef } from 'react';

// ──────────────────────────────────────────
// AdminSystemPrompts.jsx — 시스템 프롬프트 관리
// 좌우 분할: 왼쪽 프롬프트 목록 | 오른쪽 내용 편집기
// ──────────────────────────────────────────

const CATEGORY_MAP = {
    SHIPPING_SYSTEM_PROMPT_KO: '핵심 추출',
    SHIPPING_SYSTEM_PROMPT_EN: '핵심 추출',
    OCR_MERGE_PROMPT: '문서 OCR',
    OCR_AUTOCORRECT_PROMPT: '문서 OCR',
    SMART_LABEL_PROMPT: '문서 OCR',
    VERIFICATION_PROMPT_PAGE: '검증',
    VERIFICATION_PROMPT_DOC: '검증',
    VLM_SYSTEM_MESSAGE: 'VLM',
    VLM_DEFAULT_SYSTEM_PROMPT: 'VLM',
    HEADER_EXTRACTION_PROMPT: '템플릿 생성',
    FIELD_RECOMMEND_PROMPT: '템플릿 생성',
    AUTO_SCHEMA_PROMPT_KO: '템플릿 생성',
    AUTO_SCHEMA_PROMPT_EN: '템플릿 생성',
    TEMPLATE_CLASSIFY_PROMPT: '분류',
    AI_GUIDE_PROMPT: '프롬프트 도구',
    PROMPT_REFINE_PROMPT: '프롬프트 도구',
    CONTEXT_CACHE_DEFAULT_PROMPT: '캐시',
    CONTEXT_CACHE_DEFAULT_PAGE_PROMPT: '캐시',
    AI_TRANSFORM_SYSTEM_PROMPT: 'AI 변환',
    AI_TRANSFORM_USER_PROMPT: 'AI 변환',
};

const CATEGORY_COLORS = {
    '핵심 추출': { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    '문서 OCR': { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    '검증': { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    'VLM': { bg: '#eef2ff', color: '#4f46e5', border: '#c7d2fe' },
    '템플릿 생성': { bg: '#fdf4ff', color: '#a855f7', border: '#e9d5ff' },
    '분류': { bg: '#ecfeff', color: '#0891b2', border: '#a5f3fc' },
    '프롬프트 도구': { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
    '캐시': { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    'AI 변환': { bg: '#f0f9ff', color: '#0284c7', border: '#bae6fd' },
    '기타': { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb' },
};

const CATEGORY_ORDER = ['핵심 추출', '문서 OCR', '검증', 'VLM', '템플릿 생성', '분류', '프롬프트 도구', '캐시', 'AI 변환', '기타'];

const AdminSystemPrompts = ({ API_BASE_URL }) => {
    const [prompts, setPrompts] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedKey, setSelectedKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [message, setMessage] = useState(null);
    const textareaRef = useRef(null);

    const fetchPrompts = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE_URL}/api/admin/system-prompts`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (json.success) {
                const data = json.data || {};
                setPrompts(data);
                const keys = Object.keys(data);
                if (keys.length > 0 && !selectedKey) {
                    setSelectedKey(keys[0]);
                    setEditValue(typeof data[keys[0]] === 'string' ? data[keys[0]] : JSON.stringify(data[keys[0]], null, 2));
                }
            }
        } catch (e) {
            console.error('Failed to fetch system prompts:', e);
            showMsg('error', '프롬프트 로드 실패');
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

    const showMsg = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    const selectPrompt = (key) => {
        if (isDirty) {
            if (!window.confirm('저장하지 않은 변경사항이 있습니다. 무시하고 이동하시겠습니까?')) return;
        }
        setSelectedKey(key);
        const val = prompts[key];
        setEditValue(typeof val === 'string' ? val : JSON.stringify(val, null, 2));
        setIsEditing(false);
        setIsDirty(false);
    };

    const handleEditChange = (val) => {
        setEditValue(val);
        setIsDirty(val !== (typeof prompts[selectedKey] === 'string' ? prompts[selectedKey] : JSON.stringify(prompts[selectedKey], null, 2)));
    };

    const handleSave = async () => {
        if (!selectedKey) return;
        setSaving(true);
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE_URL}/api/admin/system-prompts`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ key: selectedKey, value: editValue }),
            });
            const json = await res.json();
            if (json.success) {
                setPrompts(prev => ({ ...prev, [selectedKey]: editValue }));
                setIsDirty(false);
                setIsEditing(false);
                showMsg('success', '저장 완료');
            } else {
                showMsg('error', json.error || '저장 실패');
            }
        } catch (e) {
            showMsg('error', '서버 오류');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (key) => {
        if (!window.confirm(`"${key}" 프롬프트를 삭제하시겠습니까?\n\n이 프롬프트를 사용하는 기능이 동작하지 않을 수 있습니다.`)) return;
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE_URL}/api/admin/system-prompts/${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (json.success) {
                const newPrompts = { ...prompts };
                delete newPrompts[key];
                setPrompts(newPrompts);
                if (selectedKey === key) {
                    const keys = Object.keys(newPrompts);
                    if (keys.length > 0) {
                        setSelectedKey(keys[0]);
                        setEditValue(typeof newPrompts[keys[0]] === 'string' ? newPrompts[keys[0]] : '');
                    } else {
                        setSelectedKey(null);
                        setEditValue('');
                    }
                    setIsDirty(false);
                    setIsEditing(false);
                }
                showMsg('success', `"${key}" 삭제 완료`);
            } else {
                showMsg('error', json.error || '삭제 실패');
            }
        } catch (e) {
            showMsg('error', '서버 오류');
        }
    };

    const handleAdd = async () => {
        const trimmed = newKey.trim();
        if (!trimmed) { showMsg('error', '키를 입력하세요'); return; }
        if (prompts[trimmed]) { showMsg('error', '이미 존재하는 키입니다'); return; }
        setSaving(true);
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE_URL}/api/admin/system-prompts`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ key: trimmed, value: '' }),
            });
            const json = await res.json();
            if (json.success) {
                setPrompts(prev => ({ ...prev, [trimmed]: '' }));
                setSelectedKey(trimmed);
                setEditValue('');
                setIsEditing(true);
                setIsDirty(false);
                setNewKey('');
                setShowAdd(false);
                showMsg('success', `"${trimmed}" 추가됨 — 내용을 작성하세요`);
                setTimeout(() => textareaRef.current?.focus(), 100);
            } else {
                showMsg('error', json.error || '추가 실패');
            }
        } catch (e) {
            showMsg('error', '서버 오류');
        } finally {
            setSaving(false);
        }
    };

    const extractVars = (val) => {
        if (typeof val !== 'string') return [];
        return [...val.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]);
    };

    const getCategory = (key) => CATEGORY_MAP[key] || '기타';
    const getCatStyle = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS['기타'];

    // 카테고리별 그룹핑 + 필터
    const grouped = {};
    Object.entries(prompts).forEach(([key, val]) => {
        if (filter && !key.toLowerCase().includes(filter.toLowerCase())) return;
        const cat = getCategory(key);
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push({ key, val });
    });

    const selectedVal = selectedKey ? prompts[selectedKey] : null;
    const selectedVars = selectedVal ? extractVars(selectedVal) : [];
    const charCount = typeof editValue === 'string' ? editValue.length : 0;

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6b7280' }}>불러오는 중...</div>;
    }

    return (
        <div style={S.container}>
            {/* 토스트 메시지 */}
            {message && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 9999,
                    padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    ...(message.type === 'success'
                        ? { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
                        : { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }),
                }}>
                    {message.text}
                </div>
            )}

            {/* ───── 왼쪽: 프롬프트 목록 ───── */}
            <div style={S.leftPanel}>
                <div style={S.leftHeader}>
                    <span style={S.leftTitle}>시스템 프롬프트</span>
                    <span style={S.countBadge}>{Object.keys(prompts).length}</span>
                </div>

                {/* 검색 */}
                <div style={S.searchWrap}>
                    <input
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder="검색..."
                        style={S.searchInput}
                    />
                    {filter && <button onClick={() => setFilter('')} style={S.clearBtn}>✕</button>}
                </div>

                {/* 추가 버튼 */}
                <button onClick={() => setShowAdd(!showAdd)} style={S.addBtn}>
                    {showAdd ? '✕ 취소' : '+ 새 프롬프트'}
                </button>

                {/* 추가 입력 */}
                {showAdd && (
                    <div style={S.addForm}>
                        <input
                            value={newKey}
                            onChange={e => setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                            placeholder="KEY_NAME"
                            style={S.addInput}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        />
                        <button onClick={handleAdd} disabled={saving} style={S.addConfirmBtn}>추가</button>
                    </div>
                )}

                {/* 카테고리별 목록 */}
                <div style={S.listWrap}>
                    {CATEGORY_ORDER.filter(cat => grouped[cat]).map(cat => {
                        const catStyle = getCatStyle(cat);
                        return (
                            <div key={cat} style={S.catGroup}>
                                <div style={{ ...S.catLabel, color: catStyle.color }}>{cat}</div>
                                {grouped[cat].map(({ key, val }) => {
                                    const isSelected = selectedKey === key;
                                    const len = typeof val === 'string' ? val.length : 0;
                                    return (
                                        <div
                                            key={key}
                                            onClick={() => selectPrompt(key)}
                                            style={{
                                                ...S.listItem,
                                                background: isSelected ? '#f0f4ff' : 'transparent',
                                                borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
                                            }}
                                        >
                                            <div style={S.listItemName}>{key}</div>
                                            <div style={S.listItemMeta}>{len.toLocaleString()}자</div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {Object.keys(grouped).length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                            검색 결과 없음
                        </div>
                    )}
                </div>
            </div>

            {/* ───── 오른쪽: 내용 편집기 ───── */}
            <div style={S.rightPanel}>
                {selectedKey ? (
                    <>
                        {/* 헤더 */}
                        <div style={S.editorHeader}>
                            <div style={S.editorHeaderLeft}>
                                <span style={{
                                    ...S.editorCatBadge,
                                    background: getCatStyle(getCategory(selectedKey)).bg,
                                    color: getCatStyle(getCategory(selectedKey)).color,
                                    borderColor: getCatStyle(getCategory(selectedKey)).border,
                                }}>
                                    {getCategory(selectedKey)}
                                </span>
                                <span style={S.editorKeyName}>{selectedKey}</span>
                                {isDirty && <span style={S.dirtyDot} title="변경사항 있음" />}
                            </div>
                            <div style={S.editorActions}>
                                {!isEditing ? (
                                    <>
                                        <button onClick={() => { setIsEditing(true); setTimeout(() => textareaRef.current?.focus(), 50); }} style={S.editBtn}>
                                            편집
                                        </button>
                                        <button onClick={() => handleDelete(selectedKey)} style={S.deleteBtn}>삭제</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => { setIsEditing(false); setEditValue(typeof prompts[selectedKey] === 'string' ? prompts[selectedKey] : ''); setIsDirty(false); }} style={S.cancelBtn}>
                                            취소
                                        </button>
                                        <button onClick={handleSave} disabled={saving || !isDirty} style={{ ...S.saveBtn, opacity: isDirty ? 1 : 0.4 }}>
                                            {saving ? '저장 중...' : '저장'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 메타 정보 바 */}
                        <div style={S.metaBar}>
                            <span style={S.metaItem}>{charCount.toLocaleString()}자</span>
                            {selectedVars.length > 0 && (
                                <span style={S.metaVars}>
                                    변수: {selectedVars.map(v => `{{${v}}}`).join(', ')}
                                </span>
                            )}
                        </div>

                        {/* 내용 영역 */}
                        <div style={S.editorBody}>
                            {isEditing ? (
                                <textarea
                                    ref={textareaRef}
                                    value={editValue}
                                    onChange={e => handleEditChange(e.target.value)}
                                    style={S.editorTextarea}
                                    spellCheck={false}
                                />
                            ) : (
                                <pre style={S.editorPre}>{editValue}</pre>
                            )}
                        </div>
                    </>
                ) : (
                    <div style={S.emptyRight}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}></div>
                        <div style={{ color: '#9ca3af', fontSize: '15px' }}>왼쪽에서 프롬프트를 선택하세요</div>
                    </div>
                )}
            </div>
        </div>
    );
};

const S = {
    container: {
        display: 'flex', height: '100%', fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', -apple-system, sans-serif",
        background: '#fff', overflow: 'hidden',
    },

    // ── 왼쪽 패널 ──
    leftPanel: {
        width: '280px', minWidth: '280px', borderRight: '1px solid #e5e7eb',
        display: 'flex', flexDirection: 'column', background: '#fafbfc', flexShrink: 0,
    },
    leftHeader: {
        padding: '16px 16px 0', display: 'flex', alignItems: 'center', gap: '8px',
    },
    leftTitle: { fontSize: '15px', fontWeight: '700', color: '#1f2937' },
    countBadge: {
        background: '#dbeafe', color: '#2563eb', fontSize: '11px', fontWeight: '700',
        padding: '1px 7px', borderRadius: '10px',
    },
    searchWrap: { position: 'relative', padding: '10px 16px 0' },
    searchInput: {
        width: '100%', padding: '7px 28px 7px 10px', border: '1px solid #e5e7eb',
        borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
        background: '#fff',
    },
    clearBtn: {
        position: 'absolute', right: '22px', top: '50%', transform: 'translateY(-15%)',
        background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '12px',
    },
    addBtn: {
        margin: '8px 16px 0', padding: '6px', border: '1px dashed #d1d5db', borderRadius: '6px',
        background: 'transparent', cursor: 'pointer', fontSize: '12px', color: '#6b7280',
        textAlign: 'center', transition: 'all 0.15s',
    },
    addForm: {
        margin: '6px 16px 0', display: 'flex', gap: '4px',
    },
    addInput: {
        flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px',
        fontSize: '12px', outline: 'none', fontFamily: 'monospace',
    },
    addConfirmBtn: {
        padding: '6px 12px', border: 'none', borderRadius: '6px',
        background: '#18181b', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
    },
    listWrap: {
        flex: 1, overflowY: 'auto', padding: '8px 0', marginTop: '4px',
    },
    catGroup: { marginBottom: '4px' },
    catLabel: {
        padding: '4px 16px 2px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.02em',
    },
    listItem: {
        padding: '7px 16px 7px 19px', cursor: 'pointer', transition: 'background 0.1s',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    listItemName: {
        fontSize: '12.5px', fontWeight: '500', color: '#374151', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, fontFamily: 'monospace',
    },
    listItemMeta: { fontSize: '10px', color: '#9ca3af', flexShrink: 0, marginLeft: '6px' },

    // ── 오른쪽 패널 ──
    rightPanel: {
        flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    editorHeader: {
        padding: '12px 20px', borderBottom: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fff', flexShrink: 0,
    },
    editorHeaderLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
    editorCatBadge: {
        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
        border: '1px solid',
    },
    editorKeyName: { fontSize: '15px', fontWeight: '700', color: '#1f2937', fontFamily: 'monospace' },
    dirtyDot: {
        width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b',
    },
    editorActions: { display: 'flex', gap: '8px' },
    editBtn: {
        padding: '6px 16px', border: '1px solid #e5e7eb', borderRadius: '6px',
        background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontWeight: '500',
    },
    deleteBtn: {
        padding: '6px 16px', border: '1px solid #fecaca', borderRadius: '6px',
        background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#dc2626', fontWeight: '500',
    },
    cancelBtn: {
        padding: '6px 16px', border: '1px solid #e5e7eb', borderRadius: '6px',
        background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#6b7280',
    },
    saveBtn: {
        padding: '6px 20px', border: 'none', borderRadius: '6px',
        background: '#18181b', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    },
    metaBar: {
        padding: '6px 20px', background: '#f8fafc', borderBottom: '1px solid #f3f4f6',
        display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0,
    },
    metaItem: { fontSize: '12px', color: '#6b7280' },
    metaVars: { fontSize: '11px', color: '#9ca3af', fontFamily: "'Fira Code', monospace" },
    editorBody: { flex: 1, overflow: 'hidden', display: 'flex' },
    editorTextarea: {
        width: '100%', height: '100%', padding: '16px 20px', border: 'none', outline: 'none',
        fontSize: '13px', lineHeight: '1.8', resize: 'none', boxSizing: 'border-box',
        fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
        background: '#fffef8',
    },
    editorPre: {
        width: '100%', height: '100%', padding: '16px 20px', margin: 0, overflow: 'auto',
        fontSize: '13px', lineHeight: '1.8', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
        color: '#374151', boxSizing: 'border-box',
    },
    emptyRight: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center',
    },
};

export default AdminSystemPrompts;
